import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from '../dialog/dialog';
import { useMimic } from '../../hooks/useMimic';
import styles from './power-bi-capture-dialog.module.scss';

interface PowerBiCaptureDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
}

export function PowerBiCaptureDialog({
  isOpen,
  onClose,
  onSave,
}: PowerBiCaptureDialogProps) {
  const [viewName, setViewName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { isMimic, mimicUser } = useMimic();

  // Reset state each time the dialog opens
  useEffect(() => {
    if (isOpen) {
      setViewName('');
      setIsSaving(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!viewName.trim()) return;
    setIsSaving(true);
    try {
      await onSave(viewName.trim());
      onClose();
    } catch {
      // Error is surfaced via toast by the parent; keep the dialog open.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      title="Save View"
      onClose={onClose}
      width={520}
      actionsAlign="center"
      actions={[
        {
          label: 'Save',
          onClick: handleSave,
          disabled: !viewName.trim(),
          loading: isSaving,
          variant: 'primary',
        },
      ]}
    >
      <div className={styles['content']}>
        {isMimic && mimicUser && (
          <p className={styles['mimic-notice']} role="status">
            You are managing Saved Views for {mimicUser.name} while operating in
            Mimic Mode.
          </p>
        )}
        <label className={styles['input-label']} htmlFor="view-name-input">
          Enter a name for this view:
        </label>
        <input
          ref={inputRef}
          id="view-name-input"
          className={styles['input']}
          type="text"
          value={viewName}
          onChange={(e) => setViewName(e.target.value)}
          placeholder="Example: December 2019 Sales Profit"
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
        />
      </div>
    </Dialog>
  );
}
